import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { checkESignatureLogin } from '~/api/tickets';
import { Button } from '~/eds/src/components/Button/Button';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { withUsers } from '~/hocs/withUsers';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { testIsFileExtensionSupported } from '~/utils/esignature';
import {
  getTicketDocumentFileExtension,
  shouldFillFormQuestionsInSignStage,
  testHasInvalidSigners,
} from '~/utils/ticket';

function CollectSignatureAction({
  // connected
  esignatureProvider,
  hasEnvelope,
  hasEsignatureIntegration,
  ticketId,
  signStatus,
  ticket,
  users,
}) {
  const [eSignatureInfo, setESignatureInfo] = useState({ error: true });
  const { toast } = useToast();
  const intl = useIntl();

  const [collectSignatures, result] =
    api.endpoints.collectSignatures.useLazyQuery();
  const {
    isLoading: isCollectSignaturesLoading,
    error: collectSignaturesError,
  } = result;

  useEffect(() => {
    if (collectSignaturesError) {
      // if the integration is missing, we should show a warning message
      if (collectSignaturesError?.response?.status === 404) {
        toast({
          title: intl.formatMessage({
            id: 'CollectSignatureAction.missingIntegrationTitle',
            defaultMessage: 'Missing Electronic Signature Integration',
            description:
              'Toast title for missing electronic signature integration.',
          }),
          message: intl.formatMessage({
            id: 'CollectSignatureAction.missingIntegrationMessage',
            defaultMessage:
              'Please contact your system administrator to verify and activate the Electronic Signature Integration.',
            description:
              'Toast message for missing electronic signature integration.',
          }),
          status: 'warning',
          autoDismiss: false,
        });
      } else if (
        collectSignaturesError?.response?.status === 400 &&
        // TODO: we shouldn't be veryfing the error message like this, should be better parsed in BE.
        collectSignaturesError.response?.data?.data.includes('sfdt')
      ) {
        toast({
          title: intl.formatMessage({
            id: 'CollectSignatureAction.errorTitle',
            defaultMessage: 'We received an error',
            description: 'Generic error toast title.',
          }),
          message: intl.formatMessage({
            id: 'CollectSignatureAction.sfdtErrorMessage',
            defaultMessage:
              "We're unable to open this document. Please use Word on the Web or edit in Word on your computer and upload again.",
            description: 'Error message for sfdt file format issue.',
          }),
          status: 'danger',
          autoDismiss: false,
        });
      } else {
        toast({
          title: intl.formatMessage({
            id: 'CollectSignatureAction.errorTitle',
            defaultMessage: 'We received an error',
            description: 'Generic error toast title.',
          }),
          message: collectSignaturesError.response?.data?.data,
          status: 'danger',
          autoDismiss: false,
        });
      }
    }
  }, [collectSignaturesError]);

  const executeCollectSignatures = async () => {
    const result = await collectSignatures({
      ticketId,
      esignatureProvider,
      ticketUrl: window.location.href,
    }).unwrap();
    // Important that we use .unwrap() here and trigger window.open() directly
    // If we use a useEffect() that watches result, the browser will likely block the popup, since the open() is not a direct result of the user action

    if (result.redirectUrl) {
      window.open(result.redirectUrl, '_blank');
    }
  };

  useAsync(
    checkESignatureLogin,
    { ticketId, esignatureProvider, ticketUrl: window.location.href },
    {
      condition: !!esignatureProvider,
      deps: [ticketId],
      successHandler: setESignatureInfo,
      errorToastMessage: intl.formatMessage({
        id: 'CollectSignatureAction.eSigCommunicationError',
        defaultMessage:
          'There was an issue communicating with the e-signature provider. Please try again.',
        description:
          'Error message for e-signature provider communication issue.',
      }),
    },
  );

  if (
    signStatus === TicketStatusType.Signed ||
    hasEnvelope ||
    !esignatureProvider
  ) {
    return null;
  }

  const handleCollectSignaturesClick = () => {
    if (eSignatureInfo.error) {
      toast({
        message: intl.formatMessage({
          id: 'CollectSignatureAction.collectSignaturesError',
          defaultMessage:
            'Unable to collect the signatures. Please refresh the page and try again.',
          description: 'Error toast message when collecting signatures fails.',
        }),
        status: 'danger',
      });
      return;
    }

    if (eSignatureInfo.isUserEsignatureLoggedIn) {
      executeCollectSignatures();
    } else {
      const esignatureAuthenticateUrl = eSignatureInfo.redirectUrl;
      window.open(esignatureAuthenticateUrl, '_blank');
    }
  };

  if (shouldFillFormQuestionsInSignStage(ticket, users)) {
    return null;
  }

  let fileExtension = getTicketDocumentFileExtension(ticket);
  const isFileExtensionSupported = testIsFileExtensionSupported(
    fileExtension,
    esignatureProvider,
  );

  const tooltip = !hasEsignatureIntegration
    ? intl.formatMessage({
        id: 'CollectSignatureAction.noEsignatureProviderTooltip',
        defaultMessage:
          'This account has not set up an e-signature provider yet.',
        description: 'Tooltip shown when no e-signature provider is set up.',
      })
    : !isFileExtensionSupported
      ? intl.formatMessage({
          id: 'CollectSignatureAction.unsupportedFileTypeTooltip',
          defaultMessage:
            'The file type is not supported by your e-signature provider. Please upload your contract in a supported file type and try again.',
          description:
            'Tooltip shown for unsupported file types by the e-signature provider.',
        })
      : undefined;

  return (
    <Button
      disabled={
        !hasEsignatureIntegration ||
        !isFileExtensionSupported ||
        (ticket.stage === TicketStageType.Sign &&
          testHasInvalidSigners(ticket, users))
      }
      isLoading={isCollectSignaturesLoading}
      onClick={handleCollectSignaturesClick}
      text={intl.formatMessage({
        id: 'CollectSignatureAction.buttonText',
        defaultMessage: 'Collect Signatures',
        description: 'Button text to collect signatures.',
      })}
      tooltip={tooltip}
      variant="primary"
    />
  );
}

function mapStateToProps({ ticket }) {
  return {
    esignatureProvider: ticket.esignatureProvider,
    hasEnvelope: ticket.hasEnvelope,
    hasEsignatureIntegration: ticket.hasEsignatureIntegration,
    ticketId: ticket.id,
    signStatus: ticket.stages.sign?.status,
    ticket: ticket,
  };
}

export default withUsers(connect(mapStateToProps)(CollectSignatureAction));
